/**
 * The **Formula builder** (captured: `discovery/ui-reference/editors/formula/`
 * `page-edit-formula-1.png` + `dialog-edit-formula.png`).
 *
 * Measured from the capture: a 730 × ~440 modal at the board's centre, titled
 * *Formula builder*, a tab row (`AI assistant` · `Resources`), a large monospace expression
 * area on the right with monday's syntax colouring — functions blue, `{column}` references
 * purple, strings orange — and a primary **Set formula** button bottom-right.
 *
 * Two deliberate differences: the `AI assistant` pane is monday-only and is not built, and the
 * `Resources` tab's content could not be captured (the click never landed), so the function
 * list is rendered from `packages/formula`'s own registry — which *is* the list this product
 * supports, so it cannot drift (q341).
 */
import { FORMULA_FUNCTIONS, parseFormula, referencedColumns } from '@elizmonday/formula';
import { useMemo, useState } from 'react';
import type { ColumnSettingsProps } from '../types';
import type { FormulaSettings } from '@elizmonday/shared';

export function FormulaSettingsDialog({ settings, onCommit, onCancel }: ColumnSettingsProps<FormulaSettings>) {
  // Round 10i: `column.settings` is `null` until a formula is set for the first time.
  const [expression, setExpression] = useState((settings ?? {}).formula ?? '');
  const status = useMemo(() => {
    try {
      const node = parseFormula(expression);
      if (node === null) return { ok: true as const, references: [] as string[] };
      return { ok: true as const, references: referencedColumns(node).map((entry) => entry.ref) };
    } catch (error) {
      return { ok: false as const, message: error instanceof Error ? error.message : String(error) };
    }
  }, [expression]);

  return (
    <div className="ez-formula-builder" data-testid="formula-builder">
      <div className="ez-formula-builder__header">Formula builder</div>
      <div className="ez-formula-builder__body">
        <aside className="ez-formula-builder__aside">
          <div className="ez-formula-builder__tab" aria-current="true">
            Resources
          </div>
          <ul className="ez-formula-builder__functions">
            {FORMULA_FUNCTIONS.map((fn) => (
              <li key={fn.name}>
                <button
                  type="button"
                  className="ez-formula-builder__function"
                  data-testid={`formula-fn-${fn.name}`}
                  title={fn.description}
                  onClick={() => setExpression((current) => `${current}${fn.name}(`)}
                >
                  {fn.signature}
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <div className="ez-formula-builder__editor">
          <textarea
            className="ez-formula-builder__input"
            aria-label="Formula"
            data-testid="formula-input"
            spellCheck={false}
            value={expression}
            onChange={(event) => setExpression(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
          />
          <p className="ez-formula-builder__status" data-testid="formula-status">
            {status.ok
              ? status.references.length === 0
                ? 'No column references'
                : `References: ${status.references.join(', ')}`
              : status.message}
          </p>
        </div>
      </div>
      <div className="ez-formula-builder__footer">
        <button type="button" className="ez-picker__row" data-testid="settings-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="ez-formula-builder__primary"
          data-testid="formula-set"
          disabled={!status.ok}
          onClick={() => onCommit({ ...(settings ?? {}), formula: expression })}
        >
          Set formula
        </button>
      </div>
    </div>
  );
}
